import { useState, useEffect } from 'react';
import { Loader2, Calendar, Play, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StatusBadge({ enabled }) {
  if (enabled) {
    return <Badge variant="outline" className="text-green-600 border-green-400">Enabled</Badge>;
  }
  return <Badge variant="outline" className="text-amber-600 border-amber-400">Disabled</Badge>;
}

function RunStatusBadge({ status }) {
  if (status === 'ok') return <Badge variant="outline" className="text-green-600 border-green-400">OK</Badge>;
  if (status === 'error') return <Badge variant="outline" className="text-red-600 border-red-400">Error</Badge>;
  return <Badge variant="outline">{status || 'unknown'}</Badge>;
}

export function InvoiceGenerationSchedulerPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [confirmEntity, setConfirmEntity] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.invoiceGenerationStatus();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const runGeneration = async (entityId) => {
    setRunning(true);
    setConfirmEntity(null);
    try {
      const { data: result } = await diagnosticsAPI.invoiceGenerationRun(entityId);
      toast.success(`Generation complete: ${result.created || 0} created, ${result.candidates || 0} candidates`);
      loadStatus(); // refresh
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const entities = ['afpv', 'bi_immo'];

  // Compute target month (prior month)
  const now = new Date();
  const targetMonth = now.getMonth() === 0
    ? `${now.getFullYear() - 1}-12`
    : `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-500" />
          Invoice Generation Scheduler
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Monthly draft generation (2nd of each month, 07:00 UTC). Draft-only, never finalizes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        )}

        {data && (
          <>
            {/* Scheduler status */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Scheduler:</span>
                <StatusBadge enabled={data.enabled} />
              </div>
              {data.next_fire_time && (
                <div className="text-sm text-muted-foreground">
                  Next fire: {new Date(data.next_fire_time).toLocaleString()}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={loadStatus} disabled={loading}>
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
              </Button>
            </div>

            {/* Last runs */}
            {data.last_runs && data.last_runs.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Last Runs</h4>
                <div className="space-y-1">
                  {data.last_runs.map((run, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm border rounded px-3 py-2 bg-muted/30">
                      <span className="font-mono text-xs">{run.entity_id}</span>
                      <span className="text-muted-foreground">{run.period}</span>
                      <RunStatusBadge status={run.status} />
                      {run.status === 'ok' && (
                        <span className="text-muted-foreground">
                          {run.created} created, {run.skipped_already_invoiced || 0} skipped,
                          {' '}{run.candidates || 0} candidates
                        </span>
                      )}
                      {run.error && (
                        <span className="text-red-600 text-xs truncate max-w-[300px]">{run.error}</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {run.trigger} {run.finished_at ? new Date(run.finished_at).toLocaleString() : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Run Generation Now buttons */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Run Generation Now (target: {targetMonth})</h4>
              <div className="flex gap-2">
                {entities.map((eid) => (
                  <Button
                    key={eid}
                    variant="outline"
                    size="sm"
                    disabled={running}
                    onClick={() => setConfirmEntity(eid)}
                  >
                    {running ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
                    {eid}
                  </Button>
                ))}
              </div>
            </div>

            {/* Attention findings */}
            {data.findings && data.findings.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Attention Findings
                </h4>
                <div className="space-y-1">
                  {data.findings.map((f, i) => (
                    <div
                      key={i}
                      className={`text-sm border rounded px-3 py-2 ${
                        f.severity === 'critical'
                          ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
                          : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
                      }`}
                    >
                      <div className="font-medium">{f.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{f.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.findings?.length === 0 && data.last_runs?.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                No open attention findings for invoice generation.
              </div>
            )}
          </>
        )}

        {/* Confirm dialog */}
        <Dialog open={!!confirmEntity} onOpenChange={(open) => { if (!open) setConfirmEntity(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Invoice Generation</DialogTitle>
              <DialogDescription>
                Generate draft invoices for <strong>{confirmEntity}</strong> for period <strong>{targetMonth}</strong>.
                This creates DRAFTS only (never finalizes). Idempotent: already-invoiced bookings are skipped.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmEntity(null)}>Cancel</Button>
              <Button onClick={() => runGeneration(confirmEntity)}>Generate Drafts</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
